(() => {
  const money = (cents, currency = 'USD', locale = 'en-US') => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }
  };

  const initHeader = (scope = document) => {
    scope.querySelectorAll('[data-sl-header]').forEach((header) => {
      if (header.dataset.ready === 'true') return;
      header.dataset.ready = 'true';
      const button = header.querySelector('[data-sl-menu-toggle]');
      const panel = header.querySelector('[data-sl-mobile-menu]');
      if (!button || !panel) return;

      const close = () => {
        button.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        document.body.classList.remove('menu-open');
      };
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
        document.body.classList.toggle('menu-open', !open);
      });
      panel.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
      window.addEventListener('resize', () => {
        if (window.innerWidth > 990) close();
      }, { passive: true });
    });
  };

  const initQuantity = (scope = document) => {
    scope.querySelectorAll('[data-sl-quantity]').forEach((quantity) => {
      if (quantity.dataset.ready === 'true') return;
      quantity.dataset.ready = 'true';
      const input = quantity.querySelector('input');
      quantity.querySelector('[data-minus]')?.addEventListener('click', () => {
        input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) - 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      quantity.querySelector('[data-plus]')?.addEventListener('click', () => {
        input.value = String(Number(input.value || 1) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initGallery = (scope = document) => {
    scope.querySelectorAll('[data-sl-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready === 'true') return;
      gallery.dataset.ready = 'true';
      const show = (id) => {
        gallery.querySelectorAll('[data-media]').forEach((media) => {
          media.hidden = String(media.dataset.media) !== String(id);
        });
        gallery.querySelectorAll('[data-thumb]').forEach((thumb) => {
          thumb.setAttribute('aria-pressed', String(String(thumb.dataset.thumb) === String(id)));
        });
      };
      gallery.querySelectorAll('[data-thumb]').forEach((thumb) => thumb.addEventListener('click', () => show(thumb.dataset.thumb)));
      gallery.showMedia = show;
    });
  };

  const initStandardProduct = (scope = document) => {
    scope.querySelectorAll('[data-sl-standard-product]').forEach((root) => {
      if (root.dataset.ready === 'true') return;
      root.dataset.ready = 'true';
      const select = root.querySelector('[data-variant-select]');
      const idInput = root.querySelector('[data-variant-id]');
      const price = root.querySelector('[data-product-price]');
      const compare = root.querySelector('[data-product-compare]');
      const add = root.querySelector('[data-product-add]');
      const variantData = root.querySelector('[data-variant-json]');
      if (!select || !variantData) return;
      let variants = [];
      try { variants = JSON.parse(variantData.textContent); } catch (_error) { return; }

      const selectVariant = (variantId) => {
        const variant = variants.find((item) => String(item.id) === String(variantId));
        if (!variant) return;
        select.value = String(variant.id);
        idInput.value = String(variant.id);
        price.textContent = money(variant.price, root.dataset.currency, root.dataset.locale);
        if (compare) {
          compare.hidden = !(variant.compare_at_price > variant.price);
          compare.textContent = variant.compare_at_price ? money(variant.compare_at_price, root.dataset.currency, root.dataset.locale) : '';
        }
        add.disabled = !variant.available;
        add.textContent = variant.available ? 'Add to cart' : 'Sold out';
        root.querySelectorAll('[data-variant-button]').forEach((button) => {
          button.setAttribute('aria-pressed', String(String(button.dataset.variantButton) === String(variant.id)));
        });
        if (variant.featured_media?.id) root.querySelector('[data-sl-gallery]')?.showMedia?.(variant.featured_media.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };
      select.addEventListener('change', () => selectVariant(select.value));
      root.querySelectorAll('[data-variant-button]').forEach((button) => button.addEventListener('click', () => selectVariant(button.dataset.variantButton)));
    });
  };

  const initVillageBuilder = (scope = document) => {
    scope.querySelectorAll('[data-sl-builder]').forEach((builder) => {
      if (builder.dataset.ready === 'true') return;
      builder.dataset.ready = 'true';
      const list = builder.querySelector('[data-house-list]');
      const addHouse = builder.querySelector('[data-add-house]');
      const quantity = builder.querySelector('[data-house-quantity]');
      const count = builder.querySelector('[data-house-count]');
      const total = builder.querySelector('[data-house-total]');
      const addLabel = builder.querySelector('[data-village-add-label]');
      const unitPrice = Number(builder.dataset.unitPrice || 0);
      const currency = builder.dataset.currency || 'USD';
      const locale = builder.dataset.locale || 'en-US';
      const colors = [
        ['Cream', '#efe4ce'],
        ['Farmhouse Green', '#314c3b'],
        ['Burgundy', '#7b3432'],
        ['Farmhouse Blue', '#5d7680'],
        ['Grey', '#8a8882'],
        ['White', '#f8f7f2']
      ];

      const renumber = () => {
        [...list.children].forEach((house, rowIndex) => {
          const number = rowIndex + 1;
          house.querySelector('[data-house-title]').textContent = `House ${number}`;
          const remove = house.querySelector('[data-remove-house]');
          remove.hidden = list.children.length === 1;
          remove.setAttribute('aria-label', `Remove house ${number}`);
          house.querySelector('[data-house-name]').name = `properties[House ${number} Name]`;
          house.querySelector('[data-house-type]').name = `properties[House ${number} Type]`;
          house.querySelectorAll('[data-house-color]').forEach((input) => {
            input.name = `properties[House ${number} Color]`;
          });
        });
        const houseCount = Math.max(1, list.children.length);
        const formatted = money(houseCount * unitPrice, currency, locale);
        quantity.value = String(houseCount);
        count.textContent = `${houseCount} personalized ${houseCount === 1 ? 'house' : 'houses'}`;
        total.textContent = formatted;
        if (addLabel) addLabel.textContent = `Add my village — ${formatted}`;
      };

      const bindHouse = (house) => {
        if (house.dataset.ready === 'true') return;
        house.dataset.ready = 'true';
        house.querySelector('[data-remove-house]')?.addEventListener('click', () => {
          if (list.children.length <= 1) return;
          house.remove();
          renumber();
        });
      };

      const createHouse = (focus = true) => {
        const number = list.children.length + 1;
        const house = document.createElement('article');
        house.className = 'sl-house';
        house.innerHTML = `
          <div class="sl-house__head"><strong data-house-title>House ${number}</strong><button type="button" data-remove-house>Remove</button></div>
          <div class="sl-house__fields">
            <label class="sl-field"><span>Name <em>Required</em></span><input type="text" maxlength="18" placeholder="Example: Mom" required data-house-name></label>
            <label class="sl-field"><span>This house is for</span><select data-house-type><option value="Person">Person</option><option value="Dog">Dog</option><option value="Cat">Cat</option></select></label>
          </div>
          <fieldset class="sl-field sl-colors"><legend>House color <em>Required</em></legend><div class="sl-color-grid">
            ${colors.map(([name, color], index) => `<label class="sl-color"><input type="radio" value="${name}" data-house-color required ${index === (number - 1) % colors.length ? 'checked' : ''}><i style="--swatch:${color}"></i><small>${name.replace('Farmhouse ', '')}</small></label>`).join('')}
          </div></fieldset>`;
        list.appendChild(house);
        bindHouse(house);
        renumber();
        if (focus) house.querySelector('[data-house-name]')?.focus({ preventScroll: true });
      };

      addHouse?.addEventListener('click', () => createHouse(true));
      [...list.children].forEach(bindHouse);
      if (list.children.length === 0) createHouse(false);
      else renumber();
    });
  };

  const init = (scope = document) => {
    initHeader(scope);
    initQuantity(scope);
    initGallery(scope);
    initStandardProduct(scope);
    initVillageBuilder(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
